/**
 * 3D scene loading overlay.
 *
 * The scene is not ready the moment layout computation finishes: model assets
 * are still streaming in through Suspense, and the first frame still carries
 * geometry upload and shader compilation. Hiding the loader before then leaves
 * the user watching a frozen or half-built fab, so `Scene3DReadyProbe` keeps it
 * up until a frame has actually been presented.
 *
 * Motion is deliberately shared with the DXF import overlay (the `dxf-import-*`
 * classes in index.css) so both waits look like the same product.
 */
import React, { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';

export type Scene3DLoadingStage = 'layout' | 'import' | 'floorplan' | 'assets' | 'compile' | 'opening';

const STAGE_TITLE: Record<Scene3DLoadingStage, string> = {
  layout: 'Building 3D layout',
  import: 'Preparing imported layout',
  floorplan: 'Building floor plan geometry',
  assets: 'Loading 3D models',
  compile: 'Compiling 3D scene',
  opening: 'Switching to 3D view',
};

const STAGE_MESSAGE: Record<Scene3DLoadingStage, string> = {
  layout: 'Generating three-dimensional geometry from your layout…',
  import: 'Converting the imported layout into a 3D scene…',
  floorplan: 'Extruding walls and batching floor plan geometry…',
  assets: 'Streaming equipment, stocker and vehicle models…',
  compile: 'Uploading geometry to the GPU and compiling shaders…',
  opening: 'Preparing your semiconductor fab in three dimensions…',
};

/**
 * Reports readiness once the canvas has actually presented a frame.
 *
 * Mounted inside `<Canvas>` and rendered after the scene tree, so React has
 * committed the scene before the first `useFrame` runs.
 */
export const Scene3DReadyProbe: React.FC<{ onReady: () => void }> = ({ onReady }) => {
  const invalidate = useThree((state) => state.invalidate);
  const readyRef = useRef(false);
  const framesRef = useRef(0);

  // A demand-driven canvas only renders when asked, so request the frames this
  // probe needs rather than waiting for one that may never be scheduled.
  useEffect(() => {
    invalidate();
  }, [invalidate]);

  useFrame(() => {
    if (readyRef.current) return;
    framesRef.current += 1;
    // The first frames absorb shader compilation and geometry upload. Wait a few
    // presented frames before revealing so orbit does not hitch immediately after.
    if (framesRef.current < 4) {
      invalidate();
      return;
    }
    readyRef.current = true;
    onReady();
  });

  return null;
};

export const Canvas3DLoadingOverlay: React.FC<{
  stage: Scene3DLoadingStage;
  /** 0..1 when the stage reports real progress; null renders an indeterminate bar. */
  progress: number | null;
}> = ({ stage, progress }) => {
  const percent = progress === null ? null : Math.round(Math.min(1, Math.max(0, progress)) * 100);
  const prominent = stage === 'opening' || stage === 'compile';

  return (
    <div
      className="dxf-import-overlay absolute inset-0 z-[60] flex items-center justify-center px-4"
      role="status"
      aria-live="polite"
      aria-busy
    >
      <div className="dxf-import-backdrop absolute inset-0 bg-slate-900/30" />
      <div className="dxf-import-orb dxf-import-orb-a" aria-hidden />
      <div className="dxf-import-orb dxf-import-orb-b" aria-hidden />

      <div className="dxf-import-card relative w-full max-w-[380px] overflow-hidden rounded-[24px] border border-white/80 bg-white/95 px-8 py-9 shadow-[0_30px_90px_rgba(15,23,42,0.22)]">
        <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-blue-300/80 to-transparent" />

        <div className="flex flex-col items-center text-center">
          <div
            className={`relative mb-6 flex items-center justify-center ${
              prominent ? 'h-14 w-14' : 'h-12 w-12'
            }`}
            aria-hidden
          >
            <span className="dxf-import-pulse-ring dxf-import-pulse-ring-a" />
            <span className="dxf-import-pulse-ring dxf-import-pulse-ring-b" />
            <span className="dxf-import-dot h-2.5 w-2.5 rounded-full bg-blue-500" />
          </div>

          <h2 className="text-[19px] font-semibold tracking-tight text-slate-900">
            {STAGE_TITLE[stage]}
          </h2>

          <div
            key={stage}
            className="dxf-import-message mt-3 min-h-[40px] max-w-[20rem] text-[13px] leading-relaxed text-slate-600"
          >
            {STAGE_MESSAGE[stage]}
          </div>

          <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200/80">
            {percent === null ? (
              <div className="scene3d-loader-sweep h-full w-1/3 rounded-full bg-gradient-to-r from-sky-400 to-blue-600" />
            ) : (
              <div
                className="scene3d-loader-bar h-full rounded-full bg-gradient-to-r from-sky-400 to-blue-600"
                style={{ width: `${percent}%` }}
              />
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-400">
            <span className="dxf-import-dot h-1.5 w-1.5 rounded-full bg-blue-500" />
            <span className="tabular-nums font-medium text-slate-600">
              {percent === null ? 'Working' : `${percent}%`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
